import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  MdCheckCircle, MdWarning, MdMoreVert, MdEmail, MdBlock, MdPerson, MdClose, MdSchedule,
  MdInventory2, MdExpandMore, MdExpandLess, MdRestore, MdPayments,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { useReceivables } from '../../hooks/useReceivables';
import { useCalendar } from '../../context/CalendarContext';
import useTheme from '../../context/ThemeContext';
import { useTenant } from '../../context/TenantContext';
import useAuth from '../../hooks/useAuth';
import useImpersonation from '../../hooks/useImpersonation';
import { sendReceivableReminder, waiveReceivable, sendBulkReminders, getAutoReminderStatus, getWrittenOff, restoreReceivable } from '../../services/financeService';
import { useConfirm } from '../../components/common/ConfirmModal';
import useClubVocab from '../../hooks/useClubVocab';
import RecordPaymentModal from '../../components/fees/RecordPaymentModal';
import { buildCollectQueue, buildCollectWhatsAppMessage } from '../../utils/collectQueue';
import { toWhatsAppPhone, openWhatsApp } from '../../utils/whatsapp';

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// Bucket display config. Cards run current → 60+; tables run worst → best (action-first).
// NOTE: the 'days_1_30' key is the backend's bucket id (also an API query param value) and is
// NOT renamed to match its real contents — 'current' already covers 0–7 days overdue, so this
// bucket actually holds 8–30. Only the display label below reflects that; see
// backend/utils/receivablesAging.js's computeBucket for the authoritative boundary.
const BUCKET_META = {
  current:      { label: 'Current',    card: 'bg-nav-hover border-divider',    amount: 'text-secondary', days: 'text-subtle'   },
  days_1_30:    { label: '8–30 days',  card: 'bg-amber-50 border-amber-200',   amount: 'text-amber-700', days: 'text-amber-600' },
  days_31_60:   { label: '31–60 days', card: 'bg-orange-50 border-orange-200', amount: 'text-orange-700', days: 'text-orange-600' },
  days_60_plus: { label: '60+ days',   card: 'bg-red-50 border-red-200',       amount: 'text-red-700',   days: 'text-red-600'   },
};
const CARD_ORDER  = ['current', 'days_1_30', 'days_31_60', 'days_60_plus'];
const TABLE_ORDER = ['days_60_plus', 'days_31_60', 'days_1_30', 'current'];

const initialsOf = (name) =>
  (name || '?').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase()).join('') || '?';

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'just now';
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  if (days >= 1) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours >= 1) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  return 'just now';
}

function relativeFuture(dateStr) {
  if (!dateStr) return 'soon';
  const diff = new Date(dateStr).getTime() - Date.now();
  if (diff <= 0) return 'shortly';
  const hours = Math.floor(diff / (60 * 60 * 1000));
  if (hours >= 24) { const d = Math.floor(hours / 24); return `in ${d} day${d !== 1 ? 's' : ''}`; }
  if (hours >= 1) return `in ${hours} hour${hours !== 1 ? 's' : ''}`;
  const mins = Math.max(1, Math.floor(diff / (60 * 1000)));
  return `in ${mins} minute${mins !== 1 ? 's' : ''}`;
}

function Avatar({ name, url }) {
  if (url) {
    return <img src={url} alt={name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />;
  }
  return (
    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
      {initialsOf(name)}
    </div>
  );
}

// ── Waive confirmation modal ────────────────────────────────────────────────────
function WaiveModal({ target, onClose, onConfirm, pending }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 5;
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-card rounded-2xl border border-divider shadow-2xl p-6">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-bold text-secondary">Mark Uncollectable</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-nav-hover text-muted"><MdClose /></button>
        </div>
        <p className="text-sm text-muted mb-4">
          Write off <strong className="text-secondary">{target.studentName}</strong>'s fee of{' '}
          <strong className="text-secondary">{fmtNPR(target.amount)}</strong>. This marks the fee as waived and
          stops collection. A reason is recorded for the audit trail.
        </p>
        <label className="block text-xs font-semibold text-muted uppercase tracking-wide mb-1.5">
          Reason <span className="text-red-500">*</span>
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder={`e.g. ${v.member} left the club; balance is uncollectable…`}
          className="w-full px-3 py-2 rounded-xl border border-divider bg-card text-secondary placeholder:text-subtle text-sm outline-none focus:border-secondary resize-none"
        />
        <p className="text-[11px] text-subtle mt-1">Minimum 5 characters.</p>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-divider text-sm font-medium text-muted hover:bg-nav-hover transition-colors">
            Cancel
          </button>
          <button
            onClick={() => valid && onConfirm(reason.trim())}
            disabled={!valid || pending || isReadOnly}
            title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {pending ? 'Saving…' : 'Mark Uncollectable'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Row actions dropdown ──────────────────────────────────────────────────────
function RowActions({ entry, isPrimary, onRecordPayment, onWhatsAppReminder, onRemind, onWaive, onOpenStudent }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const [open, setOpen] = useState(false);
  const [pos,  setPos]  = useState(null);
  const btnRef = useRef(null);
  const close  = () => setOpen(false);

  const hasWhatsApp = !!toWhatsAppPhone(entry.studentPhone);
  const MENU_W    = 192;                          // matches w-48
  const itemCount = (isPrimary ? 4 : 3) + (hasWhatsApp ? 1 : 0);
  const MENU_H    = itemCount * 41 + 8;           // ~row height + py-1

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      // Flip above the trigger when there isn't room below (fixes the clipped 60+ bucket).
      const openUp = r.bottom + MENU_H + 8 > window.innerHeight;
      setPos({
        top:  openUp ? Math.max(8, r.top - MENU_H - 4) : r.bottom + 4,
        left: Math.max(8, r.right - MENU_W),
      });
    }
    setOpen(o => !o);
  };

  // Close on scroll/resize so the fixed menu never drifts away from its row.
  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const itemCls = 'w-full flex items-center gap-2 px-4 py-2.5 text-xs text-secondary hover:bg-nav-hover transition-colors';

  return (
    <div className="inline-block text-left">
      <button
        ref={btnRef}
        onClick={toggle}
        className="p-1.5 rounded-lg text-subtle hover:bg-nav-hover hover:text-muted transition-colors"
        title="Actions">
        <MdMoreVert />
      </button>
      {open && pos && createPortal(
        <>
          {/* click-away backdrop */}
          <button className="fixed inset-0 z-[90] cursor-default" onClick={close} tabIndex={-1} aria-hidden />
          {/* Rendered in a portal with fixed positioning so no table/overflow container can clip it. */}
          <div
            className="fixed w-48 bg-card border border-divider rounded-xl shadow-lg z-[100] py-1"
            style={{ top: pos.top, left: pos.left }}>
            <button onClick={() => { close(); onRecordPayment(entry); }} disabled={isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className={`${itemCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
              <MdPayments className="text-emerald-500" /> Record Payment
            </button>
            {hasWhatsApp && (
              <button onClick={() => { close(); onWhatsAppReminder(entry); }} className={itemCls}>
                <FaWhatsapp className="text-[#25D366]" /> Send WhatsApp Reminder
              </button>
            )}
            <button onClick={() => { close(); onRemind(entry); }} disabled={isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className={`${itemCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
              <MdEmail className="text-primary" /> Send Reminder
            </button>
            {isPrimary && (
              <button onClick={() => { close(); onWaive(entry); }} disabled={isReadOnly} title={isReadOnly ? 'Disabled: read-only support mode' : undefined} className={`${itemCls} disabled:opacity-50 disabled:cursor-not-allowed`}>
                <MdBlock className="text-red-500" /> Mark Uncollectable
              </button>
            )}
            <button onClick={() => { close(); onOpenStudent(entry); }} className={itemCls}>
              <MdPerson className="text-subtle" /> Open {v.member}
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ── Bucket table ──────────────────────────────────────────────────────────────
function BucketTable({ bucketKey, bucket, formatMonth, isPrimary, onRecordPayment, onWhatsAppReminder, onRemind, onWaive, onOpenStudent, onRemindAll, bulkPending }) {
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  if (!bucket || bucket.count === 0) return null;
  const meta = BUCKET_META[bucketKey];
  // Don't offer "Remind All" for current — those students aren't past due yet.
  const showRemindAll = bucketKey !== 'current';
  return (
    <div id={`bucket-${bucketKey}`} className="bg-card rounded-2xl border border-divider shadow-sm overflow-hidden scroll-mt-6">
      <div className="flex items-center justify-between px-4 py-3 border-b border-divider gap-3">
        <h3 className="text-sm font-bold text-secondary">{meta.label}</h3>
        <div className="flex items-center gap-3">
          <span className={`text-sm font-bold ${meta.amount}`}>{fmtNPR(bucket.total)}</span>
          {showRemindAll && (
            <button
              onClick={() => onRemindAll(bucketKey, meta.label, bucket.count)}
              disabled={bulkPending || isReadOnly}
              title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card text-secondary border border-divider text-xs font-semibold hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              {bulkPending
                ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                : <MdEmail className="text-sm" />}
              Remind All {bucket.count}
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-divider">
              {[v.member, 'Fee Month', 'Amount', 'Days Overdue', 'Last Reminder', ''].map(h => (
                <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold text-muted uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bucket.students.map(s => (
              <tr key={s.feePaymentId} className="border-b border-divider last:border-0 hover:bg-nav-hover transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <Avatar name={s.studentName} url={s.studentPhotoUrl} />
                    <div className="min-w-0">
                      <p className="font-medium text-secondary truncate">{s.studentName}</p>
                      {s.studentTxId && <p className="text-xs text-muted">{s.studentTxId}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-muted whitespace-nowrap">
                  {s.type === 'custom' ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-secondary">{s.description || 'Custom charge'}</span>
                      <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-nav-hover text-muted border border-divider">Custom</span>
                    </span>
                  ) : formatMonth(s.feeMonth)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap align-top">
                  <span className="font-semibold text-secondary">{fmtNPR(s.amount)}</span>
                  {/* Partial payer — show what's already been paid so the balance reads clearly. */}
                  {s.status === 'partial' && (
                    <span className="block text-[10px] text-muted mt-0.5">
                      Paid {fmtNPR((s.originalAmount || 0) - (s.discount || 0) - s.amount)} of {fmtNPR((s.originalAmount || 0) - (s.discount || 0))}
                    </span>
                  )}
                  {s.discount > 0 && (
                    <span className="block text-[10px] font-medium text-emerald-600 mt-0.5" title={s.discountNote || undefined}>
                      Discount {fmtNPR(s.discount)}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <span className={`text-xs font-semibold ${meta.days}`}>
                    {s.daysOverdue > 0 ? `${s.daysOverdue} days` : 'Not due yet'}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted whitespace-nowrap">{relativeTime(s.lastReminderSentAt)}</td>
                <td className="px-4 py-3 text-right">
                  <RowActions
                    entry={s}
                    isPrimary={isPrimary}
                    onRecordPayment={onRecordPayment}
                    onWhatsAppReminder={onWhatsAppReminder}
                    onRemind={onRemind}
                    onWaive={onWaive}
                    onOpenStudent={onOpenStudent}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Muted avatar for the archived (written-off) section ──────────────────────────
function MutedAvatar({ name, url }) {
  if (url) return <img src={url} alt={name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />;
  return (
    <div className="w-8 h-8 rounded-full bg-nav-hover text-muted border border-divider flex items-center justify-center text-xs font-bold flex-shrink-0">
      {initialsOf(name)}
    </div>
  );
}

// Kathmandu-local date — Intl applies the tz offset, so no manual UTC math.
const fmtKtmDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('en-US', { timeZone: 'Asia/Kathmandu', day: 'numeric', month: 'short', year: 'numeric' }) : '—';

// ── Written Off (waived fees) — collapsed archive below the aging buckets ─────────
function WrittenOffSection({ formatMonth, isPrimary }) {
  const currentTheme = useTheme()?.currentTheme;
  const v = useClubVocab();
  const { isReadOnly } = useImpersonation();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);

  const { data } = useQuery({
    queryKey: ['receivables', 'written-off'],
    queryFn: () => getWrittenOff().then(r => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const restoreMut = useMutation({
    mutationFn: (id) => restoreReceivable(id),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Fee restored');
      setRestoreTarget(null);
      // Refresh both the archive and the live buckets — the fee re-enters receivables.
      qc.invalidateQueries({ queryKey: ['receivables'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to restore fee'),
  });

  const summary = data?.summary || { total: 0, count: 0 };
  const records = data?.records || [];
  if (summary.count === 0) return null; // nothing written off — keep the view clean

  return (
    <div className="bg-card rounded-2xl border border-divider shadow-sm overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-nav-hover/40 transition-colors">
        <div className="flex items-center gap-2.5">
          <MdInventory2 className="text-muted text-lg flex-shrink-0" />
          <h3 className="text-sm font-bold text-secondary">Written Off</h3>
          <span className="text-xs font-medium text-muted bg-nav-hover border border-divider rounded-full px-2 py-0.5">
            {summary.count}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-bold text-muted">{fmtNPR(summary.total)}</span>
          {expanded
            ? <MdExpandLess className="text-muted text-lg flex-shrink-0" />
            : <MdExpandMore className="text-muted text-lg flex-shrink-0" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-divider overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-divider">
                {[v.member, 'Fee Month', 'Amount', 'Reason', 'Waived By', 'Date', ...(isPrimary ? [''] : [])].map((h, i) => (
                  <th key={h || `col-${i}`} className="text-left px-4 py-2.5 text-xs font-semibold text-muted uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map(r => (
                <tr key={r.feePaymentId} className="border-b border-divider last:border-0 hover:bg-nav-hover/40 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <MutedAvatar name={r.studentName} url={r.studentPhotoUrl} />
                      <div className="min-w-0">
                        <p className="font-medium text-secondary truncate">{r.studentName}</p>
                        {r.studentTxId && <p className="text-xs text-muted">{r.studentTxId}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted whitespace-nowrap">{formatMonth(r.feeMonth)}</td>
                  <td className="px-4 py-3 font-semibold text-secondary whitespace-nowrap">{fmtNPR(r.amount)}</td>
                  <td className="px-4 py-3 text-muted max-w-xs">
                    <span className="line-clamp-2" title={r.waivedReason || ''}>{r.waivedReason || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-muted whitespace-nowrap">{r.waivedByName}</td>
                  <td className="px-4 py-3 text-muted whitespace-nowrap">{fmtKtmDate(r.waivedAt)}</td>
                  {isPrimary && (
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setRestoreTarget(r)}
                        disabled={isReadOnly}
                        title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card text-secondary border border-divider text-xs font-semibold hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
                        <MdRestore className="text-sm" /> Restore
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {restoreTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-card rounded-2xl shadow-2xl border border-divider p-6">
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-bold text-secondary">Restore Fee</h2>
              <button onClick={() => setRestoreTarget(null)} className="p-1 rounded-lg hover:bg-nav-hover text-muted"><MdClose /></button>
            </div>
            <p className="text-sm text-muted mb-4">
              Restore <strong className="text-secondary">{restoreTarget.studentName}</strong>'s written-off fee of{' '}
              <strong className="text-secondary">{fmtNPR(restoreTarget.amount)}</strong> back to unpaid? It will re-enter
              receivables and can be collected or reminded again.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setRestoreTarget(null)}
                className="flex-1 py-2.5 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
                Cancel
              </button>
              <button
                onClick={() => restoreMut.mutate(restoreTarget.feePaymentId)}
                disabled={restoreMut.isPending || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className={`flex-1 py-2.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}>
                {restoreMut.isPending && <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                {restoreMut.isPending ? 'Restoring…' : 'Restore Fee'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────────
export default function Receivables() {
  const currentTheme = useTheme()?.currentTheme;
  const { isReadOnly } = useImpersonation();
  const v = useClubVocab();
  const { data, isLoading, error, refetch } = useReceivables();
  const { formatMonth } = useCalendar();
  const { user } = useAuth();
  const { club } = useTenant();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isPrimary = user?.adminType === 'primary' || user?.isPrimaryAdmin === true;

  const [waiveTarget, setWaiveTarget] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { key, label, count }
  const [recordTarget, setRecordTarget] = useState(null); // { studentId } — opens Record Payment in place

  // Same grouping Collect uses — a WhatsApp reminder quotes the student's FULL outstanding
  // picture (every unpaid month, total owed), not just whichever single row was clicked.
  const collectByStudent = new Map(buildCollectQueue(data).students.map(s => [String(s.studentId), s]));
  const sendWhatsAppReminder = (entry) => {
    const grouped = entry.studentId ? collectByStudent.get(String(entry.studentId)) : null;
    if (!grouped) return;
    openWhatsApp(grouped.studentPhone, buildCollectWhatsAppMessage(grouped, club?.name));
  };

  const { data: statusResp } = useQuery({
    queryKey: ['auto-reminder-status'],
    queryFn: () => getAutoReminderStatus().then(r => r.data),
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const status = statusResp || null;
  const [confirm, confirmModal] = useConfirm();

  const remindMut = useMutation({
    mutationFn: ({ id, force }) => sendReceivableReminder(id, force),
    onSuccess: (res) => { toast.success(res?.data?.message || 'Reminder sent'); qc.invalidateQueries({ queryKey: ['receivables'] }); },
    onError:   async (e, vars) => {
      // Shared cooldown: backend returns 409 if reminded within 3 days — confirm & retry with force.
      if (e.response?.status === 409 && e.response.data?.recentlyReminded) {
        if (await confirm({ title: 'Send reminder again?', message: e.response.data.message || `This ${v.member.toLowerCase()} was reminded recently. Send anyway?`, confirmLabel: 'Send anyway', destructive: false })) {
          remindMut.mutate({ id: vars.id, force: true });
        }
        return;
      }
      toast.error(e.response?.data?.message || 'Failed to send reminder');
    },
  });

  const bulkMut = useMutation({
    mutationFn: (bucket) => sendBulkReminders(bucket),
    onSuccess: (res) => {
      const sent = res?.data?.sent ?? 0;
      const failed = res?.data?.failed ?? 0;
      const skipped = res?.data?.skipped ?? 0;
      toast.success(`Sent ${sent} reminder${sent !== 1 ? 's' : ''}` +
        (skipped ? `, ${skipped} skipped (recently reminded)` : '') +
        (failed ? ` (${failed} failed)` : ''));
      setBulkConfirm(null);
      qc.invalidateQueries({ queryKey: ['receivables'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to send reminders'),
  });

  const waiveMut = useMutation({
    mutationFn: ({ id, reason }) => waiveReceivable(id, reason),
    onSuccess: (res) => {
      toast.success(res?.data?.message || 'Fee marked uncollectable');
      setWaiveTarget(null);
      qc.invalidateQueries({ queryKey: ['receivables'] });
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to waive fee'),
  });

  const scrollToBucket = (key) => {
    document.getElementById(`bucket-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-24 bg-nav-hover animate-pulse rounded-2xl" />)}
        </div>
        {[1, 2].map(i => <div key={i} className="h-48 bg-nav-hover animate-pulse rounded-2xl" />)}
      </div>
    );
  }

  // ── Error ──
  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <MdWarning className="mx-auto text-4xl text-amber-500 mb-3" />
          <p className="text-sm font-semibold text-secondary mb-2">Unable to load receivables</p>
          <p className="text-xs text-muted mb-4">There was an error fetching outstanding fees.</p>
          <button onClick={() => refetch()}
            className={`px-4 py-2 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-xs font-medium hover:opacity-90 transition-all duration-200`}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const summary = data.summary || { total: 0, studentCount: 0, buckets: {} };
  const buckets = summary.buckets || {};

  return (
    <div className="space-y-6">
      {confirmModal}
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-secondary">Receivables</h2>
        <p className="text-sm text-muted mt-0.5">Track unpaid fees, send reminders, and manage write-offs.</p>
      </div>

      {/* Empty state */}
      {summary.total === 0 ? (
        <div className="py-20 text-center">
          <MdCheckCircle className="mx-auto text-5xl text-green-400 mb-4" />
          <p className="text-base font-semibold text-secondary">All fees collected. Nothing to chase.</p>
          <p className="text-sm text-muted mt-2 max-w-md mx-auto">
            When {v.members.toLowerCase()} have unpaid fees, they'll appear here grouped by how long the debt has been outstanding.
          </p>
        </div>
      ) : (
        <>
          {/* Bucket cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {CARD_ORDER.map(key => {
              const b = buckets[key] || { total: 0, count: 0 };
              const meta = BUCKET_META[key];
              return (
                <button
                  key={key}
                  onClick={() => scrollToBucket(key)}
                  className={`text-left rounded-2xl border p-4 shadow-sm transition-all hover:shadow-md ${meta.card}`}>
                  <p className="text-xs font-medium text-muted">{meta.label}</p>
                  <p className={`text-xl font-bold mt-1 ${meta.amount}`}>{fmtNPR(b.total)}</p>
                  <p className="text-xs text-muted mt-0.5">{b.count} {b.count !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()}</p>
                </button>
              );
            })}
          </div>

          {/* Total line */}
          <p className="text-sm text-muted">
            Total outstanding: <span className="font-semibold text-secondary">{fmtNPR(summary.total)}</span>{' '}
            across {summary.studentCount} {summary.studentCount !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()}
          </p>

          {/* Tables, worst first */}
          <div className="space-y-5">
            {TABLE_ORDER.map(key => (
              <BucketTable
                key={key}
                bucketKey={key}
                bucket={buckets[key]}
                formatMonth={formatMonth}
                isPrimary={isPrimary}
                onRecordPayment={(s) => {
                  // Open the Record Payment modal in place (no navigation, no lost scroll
                  // position) — preselects this student (User id); the modal fetches their
                  // full unpaid-months list itself.
                  if (s.studentId) setRecordTarget({ studentId: s.studentId });
                }}
                onWhatsAppReminder={sendWhatsAppReminder}
                onRemind={(s) => remindMut.mutate({ id: s.feePaymentId })}
                onWaive={(s) => setWaiveTarget(s)}
                onOpenStudent={(s) => {
                  const id = s.studentProfileId || s.studentId;
                  if (id) navigate(`/admin/students?student=${id}`);
                }}
                onRemindAll={(k, label, count) => setBulkConfirm({ key: k, label, count })}
                bulkPending={bulkMut.isPending && bulkMut.variables === key}
              />
            ))}
          </div>
        </>
      )}

      {/* Written Off — archived (waived) fees; collapsed by default, muted accent */}
      <WrittenOffSection formatMonth={formatMonth} isPrimary={isPrimary} />

      {/* Auto-reminder cron status — read-only, subtle */}
      {status && (
        <p className="text-xs italic text-muted flex items-center gap-1.5">
          <MdSchedule className="text-sm flex-shrink-0" />
          {status.lastRun ? (
            <span>
              Automatic reminders run daily at 9:00 AM Nepal time. Last run: {relativeTime(status.lastRun)} —
              sent {status.lastSummary?.sent30 ?? 0} 30-day reminders and {status.lastSummary?.sent60 ?? 0} 60-day
              reminders to {status.lastSummary?.processed ?? 0} overdue fees.
            </span>
          ) : (
            <span>
              Automatic reminders run daily at 9:00 AM Nepal time. Next run: {relativeFuture(status.nextRun)}.
            </span>
          )}
        </p>
      )}

      {waiveTarget && (
        <WaiveModal
          target={waiveTarget}
          pending={waiveMut.isPending}
          onClose={() => setWaiveTarget(null)}
          onConfirm={(reason) => waiveMut.mutate({ id: waiveTarget.feePaymentId, reason })}
        />
      )}

      {recordTarget && (
        <RecordPaymentModal
          defaultPayment={recordTarget}
          onClose={() => setRecordTarget(null)}
          onSuccess={() => { setRecordTarget(null); qc.invalidateQueries({ queryKey: ['receivables'] }); }}
        />
      )}

      {bulkConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-sm bg-card rounded-2xl border border-divider shadow-2xl p-6">
            <div className="flex items-start justify-between mb-1">
              <h2 className="font-bold text-secondary">Remind All — {bulkConfirm.label}</h2>
              <button onClick={() => setBulkConfirm(null)} className="p-1 rounded-lg hover:bg-nav-hover text-muted"><MdClose /></button>
            </div>
            <p className="text-sm text-muted mb-4">
              Send a reminder to all <strong className="text-secondary">{bulkConfirm.count}</strong>{' '}
              {bulkConfirm.count !== 1 ? v.members.toLowerCase() : v.member.toLowerCase()} in <strong className="text-secondary">{bulkConfirm.label}</strong>?
              Each will receive an email with a payment link.
            </p>
            <div className="flex gap-2">
              <button onClick={() => setBulkConfirm(null)}
                className="flex-1 py-2.5 rounded-full bg-card text-secondary border border-divider text-sm font-medium hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200">
                Cancel
              </button>
              <button
                onClick={() => bulkMut.mutate(bulkConfirm.key)}
                disabled={bulkMut.isPending || isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className={`flex-1 py-2.5 rounded-full ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} text-sm font-medium hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}>
                {bulkMut.isPending && <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />}
                {bulkMut.isPending ? 'Sending…' : 'Send Reminders'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
